import { useMatches } from "react-router-dom";

import { useAiSettings } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import { useAskPyTriggerVisible } from "./useAskPyTriggerVisible";
import { useCompetencyAgentVisible } from "./useCompetencyAgentVisible";
import { useEnhancedAiPanelEnabled } from "./useEnhancedAiPanelEnabled";
import { useFormBuildingCoachEnabled } from "./useFormBuildingCoachEnabled";
import { useGoalCoachEnabled } from "./useGoalCoachEnabled";

import { isInIframe } from "../../../utils/isInIframe";

import RouterTags from "../../../constants/RouterTags";
import { useAskPerformYardStore } from "../AskPerformYardStore";

/**
 * Check if the legacy Ask PerformYard FAB surfaces should be visible (also used by FeedbackDrawers for positioning).
 * The docked panel replaces them wherever PY-12464's gate is satisfied; everywhere else the FAB keeps working,
 * including the goal and form-building coaches, whose popups are anchored to it.
 */
export const useAskPerformYardVisible = (): boolean => {
	const { value: aiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const goalCoachEnabled = useGoalCoachEnabled();
	const formBuildingCoachEnabled = useFormBuildingCoachEnabled();
	const competencyAgentVisible = useCompetencyAgentVisible();
	const { data: aiSettings } = useAiSettings();
	const replacedByPanel = useEnhancedAiPanelEnabled();
	const triggerVisible = useAskPyTriggerVisible();

	const askHrEnabled = aiSettings?.conversationalAiSettings?.askHrEnabled ?? false;
	const aiAssistedGoalCreationEnabled = aiSettings?.conversationalAiSettings?.goalCreationEnabled ?? false;
	const formCreationEnabled = aiSettings?.conversationalAiSettings?.formCreationEnabled ?? false;

	const matches = useMatches();
	const tags: string[] =
		matches.length > 0 ? ((matches[matches.length - 1]?.handle as { tags?: string[] })?.tags ?? []) : [];
	const hiddenByRoute = tags.includes(RouterTags.NO_ASK_PERFORMYARD);
	const forceHidden = useAskPerformYardStore((state) => state.forceHidden);
	const isOnCompetencyPage = useAskPerformYardStore((state) => state.isOnCompetencyPage);

	// The panel carries the coaches as page-contextual quick actions, so wherever it actually renders the
	// FAB and its popups stand down — otherwise both are on screen at once on a coach page. This keys off
	// the trigger rather than replacedByPanel so a company whose settings leave the panel unrendered
	// (conversational off, say) still gets the FAB, which is the coaches' only remaining home.
	const shouldHide = isInIframe() || hiddenByRoute || forceHidden || triggerVisible;

	// Two independent paths (PY-12781): Conversational AI with one of its own settings, OR
	// Coaching with one of its non-meeting settings. The coaching path is deliberately not
	// scoped to the goal/form-builder pages — the trigger has to be reachable everywhere so
	// Ask PY can ship in the Development package without the Conversational AI flag. Pre-
	// meeting prep and post-meeting analysis are excluded: neither runs through Ask PY.
	const coachingAgentEnabled = goalCoachEnabled || formBuildingCoachEnabled;

	return (
		aiEnabled &&
		!shouldHide &&
		// replacedByPanel still narrows the conversational clause on its own: a user the panel skips for
		// want of the form permission would otherwise fall back to the FAB under the new nav.
		((conversationalEnabled &&
			!replacedByPanel &&
			(askHrEnabled || aiAssistedGoalCreationEnabled || formCreationEnabled)) ||
			coachingAgentEnabled ||
			(isOnCompetencyPage && competencyAgentVisible))
	);
};
